import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Check, Download } from "lucide-react";
import { BRAND } from "@/lib/brand";

const INK = "#141311";
const BRONZE = "#A89880";
const MUTED = "#8A8071";
const LINEN = "#EFE9E2";

const POINTS = [
  "The PHFF method — protein, healthy fat, fiber & color at every meal",
  "Hand-portion cues, so it scales to YOUR body — no measuring cups, no weighing",
  "The Three-Second Check to know your plate is balanced before the first bite",
  "Print it, stick it on the fridge, and let it do the thinking for you",
];

export default function PlateGuide() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [instagram, setInstagram] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/lead-magnet", { email, name, instagram, origin_url: window.location.origin, magnet: "plate" });
      localStorage.setItem("ww_guide_popup_seen", "1");
      setDone(true);
      toast.success("Your guide is on its way to your inbox");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen lp-body" style={{ background: "#FFFFFF", color: INK }} data-testid="plate-guide-page">
      <header className="px-5 md:px-12 py-4 border-b" style={{ borderColor: "rgba(20,19,17,0.07)" }}>
        <Link to="/" className="flex items-baseline gap-2" data-testid="plate-logo">
          <span className="font-script" style={{ fontSize: "2rem", color: INK }}>{BRAND.name}</span>
          <span className="lp-label-sm" style={{ color: BRONZE }}>{BRAND.tag}</span>
        </Link>
      </header>

      <main className="max-w-[720px] mx-auto px-6 md:px-12 py-16 md:py-24 text-center">
        <div className="lp-rule mx-auto" />
        <p className="lp-label-sm mt-6" style={{ color: BRONZE }}>Free fridge guide · The PHFF Method</p>
        <h1 className="lp-h mt-6" style={{ fontSize: "clamp(2.6rem, 6vw, 4.6rem)" }}>
          How to build<br />your plate.
        </h1>
        <p className="lp-it text-2xl md:text-3xl mt-3" style={{ color: BRONZE }}>
          balanced meals, no measuring cups.
        </p>
        <p className="mt-8 text-base md:text-lg leading-relaxed max-w-lg mx-auto" style={{ color: MUTED }}>
          The exact one-page guide I teach in my nutrition workshops — how to build a
          hormone-friendly, blood-sugar-steady plate in seconds, using nothing but your own hand.
        </p>

        <ul className="mt-10 space-y-3 text-left max-w-md mx-auto">
          {POINTS.map((p) => (
            <li key={p} className="flex items-start gap-3 text-[15px]" style={{ color: "#4A453D" }}>
              <Check size={15} className="mt-1 flex-shrink-0" style={{ color: BRONZE }} /> {p}
            </li>
          ))}
        </ul>

        <div className="mt-12 p-7 md:p-10" style={{ background: LINEN }}>
          {done ? (
            <div data-testid="plate-success">
              <p className="lp-it text-2xl" style={{ color: INK }}>It's on its way to your inbox —</p>
              <p className="text-sm mt-2" style={{ color: MUTED }}>and you can grab it right now:</p>
              <a href="/guides/build-your-plate.pdf" target="_blank" rel="noreferrer" className="lp-btn lp-btn-fill mt-6" data-testid="plate-download-btn">
                <Download size={13} /> Download the guide <ArrowRight size={12} />
              </a>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="plate-form">
              <input className="input-luxe" placeholder="First name" value={name}
                onChange={(e) => setName(e.target.value)} style={{ background: "#FFFFFF" }} data-testid="plate-name" />
              <input className="input-luxe" required placeholder="Instagram handle (@you)" value={instagram}
                onChange={(e) => setInstagram(e.target.value)} style={{ background: "#FFFFFF" }} data-testid="plate-instagram" />
              <input className="input-luxe" type="email" required placeholder="Your email" value={email}
                onChange={(e) => setEmail(e.target.value)} style={{ background: "#FFFFFF" }} data-testid="plate-email" />
              <button className="lp-btn lp-btn-fill whitespace-nowrap" disabled={busy} data-testid="plate-submit">
                {busy ? "Sending…" : "Send my guide"} <ArrowRight size={12} />
              </button>
            </form>
          )}
        </div>

        <p className="lp-label-sm mt-8" style={{ color: MUTED }}>Free · Instant download · No spam, ever</p>

        <div className="mt-16 pt-10 border-t" style={{ borderColor: "rgba(20,19,17,0.1)" }}>
          <p className="lp-it text-xl" style={{ color: MUTED }}>Want the full transformation, not just the plate?</p>
          <Link to="/" className="lp-btn lp-btn-outline mt-5" data-testid="plate-to-home">
            Explore the Metabolic Transformation Method <ArrowRight size={12} />
          </Link>
        </div>
      </main>

      <footer className="py-10 text-center">
        <p className="lp-label-sm" style={{ color: "#B0A794" }}>© 2026 · {BRAND.handle} · <Link to="/privacy" className="hover:underline">Privacy</Link></p>
      </footer>
    </div>
  );
}
